import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Card,
  CardContent,
  Rating,
  Grid,
  CircularProgress,
  Alert,
  Stack,
  Chip
} from '@mui/material';
import {
  SentimentVerySatisfied as SentimentVerySatisfiedIcon,
  SentimentSatisfied as SentimentSatisfiedIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon
} from '@mui/icons-material';
import feedbackService, { Feedback } from '../../services/feedbackService';

// Custom Rating display component
const ReadOnlyRating: React.FC<{ value: number; label: string }> = ({ value, label }) => {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Rating
        value={value}
        readOnly
        size="small"
        precision={0.5}
        icon={<SentimentVerySatisfiedIcon fontSize="inherit" color="primary" />}
        emptyIcon={<SentimentSatisfiedIcon fontSize="inherit" color="disabled" />}
      />
    </Box>
  );
};

// Calculate average ratings from feedback data
const calculateAverages = (feedbackList: Feedback[]) => {
  if (!feedbackList.length) return { 
    overall: 0, 
    cleanliness: 0, 
    service: 0, 
    food: 0, 
    value: 0,
    recommendationRate: 0
  };
  
  let overall = 0, cleanliness = 0, service = 0, food = 0, value = 0;
  
  feedbackList.forEach(item => {
    overall += item.overall_rating;
    cleanliness += item.cleanliness_rating;
    service += item.service_rating;
    food += item.food_rating;
    value += item.value_rating;
  });
  
  const count = feedbackList.length;
  
  return {
    overall: parseFloat((overall / count).toFixed(1)),
    cleanliness: parseFloat((cleanliness / count).toFixed(1)),
    service: parseFloat((service / count).toFixed(1)),
    food: parseFloat((food / count).toFixed(1)),
    value: parseFloat((value / count).toFixed(1)),
    recommendationRate: Math.round((feedbackList.filter(f => f.would_recommend).length / count) * 100)
  };
};

const FeedbackAnalytics: React.FC = () => {
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [averages, setAverages] = useState({
    overall: 0,
    cleanliness: 0,
    service: 0,
    food: 0,
    value: 0,
    recommendationRate: 0
  });
  
  // Fetch feedback data
  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        setLoading(true);
        const response = await feedbackService.getFeedbacks({
          page: page + 1,
          page_size: rowsPerPage
        });
        
        setFeedback(response.results);
        setTotalCount(response.count);
        
        // Calculate averages
        const averageRatings = calculateAverages(response.results);
        setAverages(averageRatings);
      } catch (err) {
        console.error('Error fetching feedback:', err);
        setError('Failed to load feedback data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFeedback();
  }, [page, rowsPerPage]);
  
  // Handle pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Guest Feedback Analytics
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Analytics Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Average Ratings
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Typography variant="h3" color="primary" sx={{ mr: 2 }}>
                  {averages.overall}
                </Typography>
                <Box>
                  <Rating
                    value={averages.overall}
                    readOnly
                    precision={0.1}
                    size="large"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Overall average from {totalCount} reviews
                  </Typography>
                </Box>
              </Box>
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ReadOnlyRating value={averages.cleanliness} label="Cleanliness" />
                  <ReadOnlyRating value={averages.service} label="Service & Staff" />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <ReadOnlyRating value={averages.food} label="Food & Beverage" />
                  <ReadOnlyRating value={averages.value} label="Value for Money" />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recommendation Rate
              </Typography>
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h2" color="primary" gutterBottom>
                  {averages.recommendationRate}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  of guests would recommend our properties
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Stack direction="row" spacing={2}>
                    <Chip
                      icon={<ThumbUpIcon />}
                      label="Would Recommend"
                      color="success"
                      variant="outlined"
                    />
                    <Chip
                      icon={<ThumbDownIcon />}
                      label="Would Not Recommend"
                      color="error"
                      variant="outlined"
                    />
                  </Stack>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Feedback Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Typography variant="h6" sx={{ p: 2 }}>
          Recent Feedback
        </Typography>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Guest</TableCell>
                <TableCell>Property</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="center">Overall</TableCell>
                <TableCell align="center">Cleanliness</TableCell>
                <TableCell align="center">Service</TableCell>
                <TableCell align="center">Food</TableCell>
                <TableCell align="center">Value</TableCell>
                <TableCell align="center">Recommend</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : feedback.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    No feedback data available.
                  </TableCell>
                </TableRow>
              ) : (
                feedback.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.guest_name}</TableCell>
                    <TableCell>{item.property_name}</TableCell>
                    <TableCell>{formatDate(item.created_at)}</TableCell>
                    <TableCell align="center">
                      <Rating value={item.overall_rating} readOnly size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Rating value={item.cleanliness_rating} readOnly size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Rating value={item.service_rating} readOnly size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Rating value={item.food_rating} readOnly size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Rating value={item.value_rating} readOnly size="small" />
                    </TableCell>
                    <TableCell align="center">
                      {item.would_recommend ? (
                        <Chip
                          size="small"
                          icon={<ThumbUpIcon fontSize="small" />}
                          label="Yes"
                          color="success"
                        />
                      ) : (
                        <Chip
                          size="small"
                          icon={<ThumbDownIcon fontSize="small" />}
                          label="No"
                          color="error"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default FeedbackAnalytics; 